<template>
  <div>

    <b-row class="mb-1">
      <b-col>
        <h2 class="mt-25">
          {{ capitalizeFirstLetter(currentMonth.format('MMMM YYYY')) }}
        </h2>
      </b-col>
      <b-col class="text-right">
        <b-button
          class="mr-1"
          variant="flat-secondary"
          @click="generateSchedule()"
        >
          <feather-icon icon="MagicWandIcon" />
        </b-button>
        <b-button
          v-visible-to-role="['ROLE_SUPER_ADMIN']"
          class="mr-1"
          variant="flat-secondary"
          @click="toggleSelectorType"
        >
          <feather-icon :icon="selectorType === 'buttons' ? 'AlignJustifyIcon' : 'ListIcon'" />
        </b-button>
        <b-btn-group>
          <b-button
            variant="secondary"
            @click="prevMonth"
          >
            <feather-icon icon="ChevronLeftIcon" />
          </b-button>
          <b-button
            variant="secondary"
            :disabled="currentMonth.subtract(6, 'months').isAfter(dayjs())"
            @click="nextMonth"
          >
            <feather-icon icon="ChevronRightIcon" />
          </b-button>
        </b-btn-group>
      </b-col>
    </b-row>

    <div
      class="accordion"
      role="tablist"
    >
      <b-card
        v-for="day in days"
        :key="`day-button-${day.formatted}`"
        no-body
        class="mb-1"
      >
        <b-card-header
          header-tag="header"
          class="p-1 d-block"
          role="tab"
        >
          <b-row>
            <b-col>
              <b-button
                v-b-toggle="`accordion-${day.formatted}`"
                block
                variant="flat-primary"
                class="text-left pl-50 font-medium-1"
              >
                {{ capitalizeFirstLetter(day.date.format('dddd')) }},
                <span class="text-nowrap">{{ day.date.format('D MMMM YYYY') }}</span>
                <b-avatar
                  v-if="day.isFulfilled !== null"
                  :variant="day.isFulfilled ? 'success' : 'light-secondary'"
                  pill
                  size="sm"
                  class="ml-50 mt-n25 position-absolute"
                >
                  <feather-icon :icon="day.isFulfilled ? 'CheckIcon' : 'XIcon'" />
                </b-avatar>
              </b-button>
            </b-col>
            <b-col
              cols="2"
              md="1"
            >
              <b-button
                class="font-medium-1 px-0 "
                variant="flat-secondary"
                block
                @click="generateSchedule(day.date)"
              >
                <feather-icon icon="MagicWandIcon" />
              </b-button>
            </b-col>
          </b-row>
        </b-card-header>

        <b-collapse
          :id="`accordion-${day.formatted}`"
          accordion="days-accordion"
          visible
          @show="ensureDayOptionsLoaded(day.formatted)"
          @shown="scrollToCollapse(`accordion-${day.formatted}`)"
        >
          <b-tabs
            pills
            class="mx-2 mb-2"
            @activate-tab="ensureDayOptionsLoaded(day.formatted)"
          >
            <b-tab
              v-for="(shiftDefinition,shiftDefinitionKey) in shiftDefinitions"
              :key="`shift-definition-${shiftDefinition.id}`"
            >
              <template #title>
                <div class="d-inline-block mr-50">
                  {{ $t('schedule.shift') }} {{ shiftDefinition.name }}
                </div>
                <b-badge
                  v-if="getScheduleForDay(day, shiftDefinition).isFulfilled !== null"
                  :variant="getScheduleForDay(day, shiftDefinition).isFulfilled ? 'success' : 'secondary'"
                  pill
                  size="sm"
                >
                  <feather-icon
                    :icon="getScheduleForDay(day, shiftDefinition).isFulfilled ? 'CheckIcon' : 'XIcon'"
                    class="mr-0"
                  />
                </b-badge>
              </template>
              <rc-overlay :show="day.loadingSchedules">
                <b-row align-v="stretch">
                  <b-col
                    v-for="(roleShifts, role) in getScheduleForDay(day, shiftDefinition).roles"
                    :key="`role-shifts-${day.formatted}-${shiftDefinitionKey}-${role}`"
                    md="6"
                    class="mb-2"
                  >
                    <div class="border rounded p-1 h-100">
                      <h4>
                        {{ $t('roles.plural.' + role) }}
                        <b-badge :variant="roleShifts.headcounts !== roleShifts.plannedShifts.length ? 'danger' : 'success'">
                          {{ roleShifts.plannedShifts.length }} / {{ roleShifts.headcounts }}
                        </b-badge>
                      </h4>
                      <shift-worker-selector
                        v-for="(plannedShift, plannedShiftIndex) in roleShifts.plannedShifts"
                        :key="`plannedShift-${shiftDefinition.id}-${day.formatted}-${shiftDefinitionKey}-${role}-${plannedShiftIndex}`"
                        :type="selectorType"
                        :selected-option="plannedShift"
                        :options="getVisibleRoleOptions(roleShifts)"
                        :has-selectable-options="hasSelectableRoleOptions(roleShifts)"
                        :loading="day.loadingSchedules"
                        @select="handleRoleOptionSelected($event, role)"
                        @clear="handleSelectedRoleInput(null, $event)"
                        @change="handleRoleOptionChanged($event, role)"
                      />

                      <shift-worker-selector
                        v-if="roleShifts.plannedShifts.length < roleShifts.headcounts"
                        :key="`add-role-${shiftDefinition.id}-${day.formatted}-${shiftDefinitionKey}-${role}`"
                        :type="selectorType"
                        :selected-option="null"
                        :options="getVisibleRoleOptions(roleShifts)"
                        :has-selectable-options="hasSelectableRoleOptions(roleShifts)"
                        :loading="day.loadingSchedules"
                        @select="handleRoleOptionSelected($event, role)"
                      />
                    </div>
                  </b-col>

                  <b-col
                    v-for="(teamShift, teamDefinitionId) in getScheduleForDay(day, shiftDefinition).teams"
                    :key="`team-shifts-${day.formatted}-${shiftDefinitionKey}-${teamDefinitionId}`"
                    md="4"
                    class="mb-2"
                  >
                    <div class="border rounded p-1 h-100">
                      <h4 class="mb-0">
                        {{ $t('team.title') }}: {{ teamShift.team.code }}
                        <b-avatar
                          v-if="allMembersSelected(teamShift)"
                          variant="success"
                          pill
                          size="sm"
                        >
                          <feather-icon icon="CheckIcon" />
                        </b-avatar>
                      </h4>
                      <div
                        v-if="showDriverIsParamedicToggle(teamShift)"
                        class="mt-50 mb-1"
                      >
                        <b-form-checkbox
                          v-model="teamShift.driverIsParamedic"
                          @change="() => handleDriverIsParamedicToggle({
                            value: teamShift.driverIsParamedic,
                            dayFormatted: day.formatted,
                            shiftDefinitionId: resolveShiftDefinitionId(shiftDefinition),
                            teamDefinitionId,
                          })"
                        >
                          {{ $t('popups.user.init-team.driver-is-paramedic') }}
                        </b-form-checkbox>
                      </div>
                      <template v-for="(member, memberKey) in teamShift.members">
                        <div
                          v-if="shouldDisplayTeamMember(teamShift, member)"
                          :key="`role-for-team-scheduling-${day.formatted}-${shiftDefinitionKey}-${teamDefinitionId}-${memberKey}`"
                          class="mb-1"
                        >
                          <div class="d-flex align-items-center justify-content-between">
                            <h5 class="mb-0">
                              {{ getTeamMemberLabel(teamShift, member) }}
                            </h5>
                          </div>
                          <shift-worker-selector
                            :type="selectorType"
                            :selected-option="member.selectedOption"
                            :options="getVisibleMemberOptions(member)"
                            :has-selectable-options="hasSelectableMemberOptions(member)"
                            :loading="day.loadingSchedules"
                            @select="handleTeamMemberOptionSelected($event, member, teamDefinitionId, teamShift)"
                            @clear="handleSelectedTeamMemberInput(null, $event)"
                            @change="handleTeamMemberOptionChanged($event, member, teamDefinitionId, teamShift)"
                          />
                        </div>
                      </template>
                    </div>
                  </b-col>

                </b-row>
              </rc-overlay>
            </b-tab>
          </b-tabs>
        </b-collapse>
      </b-card>
    </div>
  </div>
</template>
<script>
import setup from '@/views/schedule/planner/ShiftsPlanner/setup'
import RcOverlay from '@/layouts/components/rc/RcOverlay.vue'
import ShiftWorkerSelector from '@/views/schedule/planner/ShiftsPlanner/components/ShiftWorkerSelector.vue'

const SELECTOR_TYPE_STORAGE_KEY = 'schedule-planner-selector-type'

export default {
  components: { RcOverlay, ShiftWorkerSelector },
  setup,
  data() {
    return {
      selectorType: 'select',
    }
  },
  mounted() {
    const orgDefault = this.$store.getters['appConfig/config']?.schedule?.workerSelectorType || 'select'
    const saved = localStorage.getItem(SELECTOR_TYPE_STORAGE_KEY)
    this.selectorType = (saved === 'buttons' || saved === 'select') ? saved : orgDefault
  },
  methods: {
    toggleSelectorType() {
      this.selectorType = this.selectorType === 'buttons' ? 'select' : 'buttons'
      localStorage.setItem(SELECTOR_TYPE_STORAGE_KEY, this.selectorType)
    },
  },
}
</script>
